import * as operations from './operations';

import { useQuery, useMutation, UseQueryOptions, UseMutationOptions } from '@tanstack/react-query';
import { graphqlFetcher } from '../graphql-fetcher';

export const DeliveryOptionSelectionFieldsFragmentDoc = `
    fragment DeliveryOptionSelectionFields on DeliveryOptionSelectionObject {
  id
  deliveryId
  optionId
  optionCode
  name
  clientPrice
  driverReportedAmount
  files
  input
  requireInput
  requiresFile
  requiresMultipleFiles
  fileType
  createdAt
}
    `;
export const VehicleSectionFieldsFragmentDoc = `
    fragment VehicleSectionFields on VehicleObject {
  id
  type
  marque
  model
  vehicleCode
  vin
  carType
  toRetrieve
  isDelivered
  imagesBefore
  imagesAfter
  essenceBefore
  essenceAfter
  mileageBefore
  mileageAfter
  pickedUpAt
  actualPickedUpAt
  actualArrivedAt
  deliveredAt
}
    `;
export const EndPointSectionFieldsFragmentDoc = `
    fragment EndPointSectionFields on DeliveryObject {
  vehicles {
    ...VehicleSectionFields
  }
}
    `;
export const AddressFieldsFragmentDoc = `
    fragment AddressFields on AddressObject {
  id
  country
  place_id
  address
  addressPrecision
  city
  postalCode
  latitude
  longitude
}
    `;
export const DeliveryAddressFieldsFragmentDoc = `
    fragment DeliveryAddressFields on DeliveryObject {
  fromAddress {
    ...AddressFields
  }
  toAddress {
    ...AddressFields
  }
  toReturnFromAddress {
    ...AddressFields
  }
  toReturnToAddress {
    ...AddressFields
  }
}
    `;
export const SummaryPartyFieldsFragmentDoc = `
    fragment SummaryPartyFields on DeliveryObject {
  representativeFirstName
  representativeLastName
  representativePhone
  representativeEmail
  representativeCompany
  representativeSiret
  clientFirstName
  clientLastName
  clientPhone
  clientEmail
  clientSiret
  clientCompany
  clientDrivingLicenceNumber
}
    `;
export const SummaryAvailabilityFieldsFragmentDoc = `
    fragment SummaryAvailabilityFields on DeliveryObject {
  carAvailabilityDate
  carPickUpDate
  carDeliveryDeadline
}
    `;
export const SummaryDriverFieldsFragmentDoc = `
    fragment SummaryDriverFields on DeliveryObject {
  selectedDriver {
    id
    firstName
    lastName
    phoneNumber
    email
    avatar
  }
}
    `;
export const DeliveryOptionFieldsFragmentDoc = `
    fragment DeliveryOptionFields on DeliveryOptionObject {
  id
  code
  name
  defaultClientPrice
  visibilityMode
  requireInput
  requiresFile
  requiresMultipleFiles
  dynamicPricing
  fileType
  active
  sortOrder
  createdAt
  updatedAt
}
    `;
export const DeliveryOptionOrganizationFieldsFragmentDoc = `
    fragment DeliveryOptionOrganizationFields on DeliveryOptionOrganizationObject {
  organizationId
  optionId
  clientPrice
  enabled
  displayName
  createdAt
  updatedAt
  organization {
    slug
    name
    logo
  }
}
    `;
export const ResolvedDeliveryOptionFieldsFragmentDoc = `
    fragment ResolvedDeliveryOptionFields on ResolvedDeliveryOptionObject {
  id
  code
  name
  clientPrice
  visibilityMode
  requireInput
  requiresFile
  requiresMultipleFiles
  dynamicPricing
  fileType
  sortOrder
}
    `;
export const DeliveryOptionAssignmentEnrichedFieldsFragmentDoc = `
    fragment DeliveryOptionAssignmentEnrichedFields on DeliveryOptionAssignmentEnrichedObject {
  organizationId
  optionId
  code
  name
  visibilityMode
  requireInput
  requiresFile
  requiresMultipleFiles
  dynamicPricing
  fileType
  sortOrder
  optionActive
  enabled
  effectiveClientPrice
}
    `;
export const FindAllAdminsDocument = `
    query FindAllAdmins($filter: AdminFilterInput!) {
  findAllAdmins(filter: $filter) {
    id
    sub
    email
    firstName
    lastName
    status
    createdAt
    updatedAt
    rank
  }
}
    `;

export const useFindAllAdminsQuery = <
      TData = operations.FindAllAdminsQuery,
      TError = unknown
    >(
      variables: operations.FindAllAdminsQueryVariables,
      options?: Omit<UseQueryOptions<operations.FindAllAdminsQuery, TError, TData>, 'queryKey'> & { queryKey?: UseQueryOptions<operations.FindAllAdminsQuery, TError, TData>['queryKey'] }
    ) => {
    
    return useQuery<operations.FindAllAdminsQuery, TError, TData>(
      {
    queryKey: ['FindAllAdmins', variables],
    queryFn: graphqlFetcher<operations.FindAllAdminsQuery, operations.FindAllAdminsQueryVariables>(FindAllAdminsDocument, variables),
    ...options
  }
    )};

useFindAllAdminsQuery.document = FindAllAdminsDocument;

useFindAllAdminsQuery.getKey = (variables: operations.FindAllAdminsQueryVariables) => ['FindAllAdmins', variables];

export const FindAdminBySubDocument = `
    query findAdminBySub($GetBySubInput: GetBySubInput!) {
  findAdminBySub(GetBySubInput: $GetBySubInput) {
    id
    sub
    email
    firstName
    lastName
    status
    createdAt
    updatedAt
    rank
  }
}
    `;

export const useFindAdminBySubQuery = <
      TData = operations.FindAdminBySubQuery,
      TError = unknown
    >(
      variables: operations.FindAdminBySubQueryVariables,
      options?: Omit<UseQueryOptions<operations.FindAdminBySubQuery, TError, TData>, 'queryKey'> & { queryKey?: UseQueryOptions<operations.FindAdminBySubQuery, TError, TData>['queryKey'] }
    ) => {
    
    return useQuery<operations.FindAdminBySubQuery, TError, TData>(
      {
    queryKey: ['findAdminBySub', variables],
    queryFn: graphqlFetcher<operations.FindAdminBySubQuery, operations.FindAdminBySubQueryVariables>(FindAdminBySubDocument, variables),
    ...options
  }
    )};

useFindAdminBySubQuery.document = FindAdminBySubDocument;

useFindAdminBySubQuery.getKey = (variables: operations.FindAdminBySubQueryVariables) => ['findAdminBySub', variables];

export const DeleteAdminDocument = `
    mutation deleteAdmin($DeleteInput: DeleteInput!) {
  deleteAdmin(DeleteInput: $DeleteInput) {
    id
  }
}
    `;

export const useDeleteAdminMutation = <
      TError = unknown,
      TContext = unknown
    >(options?: UseMutationOptions<operations.DeleteAdminMutation, TError, operations.DeleteAdminMutationVariables, TContext>) => {
    
    return useMutation<operations.DeleteAdminMutation, TError, operations.DeleteAdminMutationVariables, TContext>(
      {
    mutationKey: ['deleteAdmin'],
    mutationFn: (variables?: operations.DeleteAdminMutationVariables) => graphqlFetcher<operations.DeleteAdminMutation, operations.DeleteAdminMutationVariables>(DeleteAdminDocument, variables)(),
    ...options
  }
    )};

export const CreateAdminDocument = `
    mutation createAdmin($CreateAdminInput: CreateAdminInput!) {
  createAdmin(CreateAdminInput: $CreateAdminInput) {
    id
    sub
    email
    firstName
    lastName
    status
    createdAt
    updatedAt
    rank
  }
}
    `;

export const useCreateAdminMutation = <
      TError = unknown,
      TContext = unknown
    >(options?: UseMutationOptions<operations.CreateAdminMutation, TError, operations.CreateAdminMutationVariables, TContext>) => {
    
    return useMutation<operations.CreateAdminMutation, TError, operations.CreateAdminMutationVariables, TContext>(
      {
    mutationKey: ['createAdmin'],
    mutationFn: (variables?: operations.CreateAdminMutationVariables) => graphqlFetcher<operations.CreateAdminMutation, operations.CreateAdminMutationVariables>(CreateAdminDocument, variables)(),
    ...options
  }
    )};

export const UpdateAdminDocument = `
    mutation updateAdmin($UpdateAdminInput: UpdateAdminInput!) {
  updateAdmin(UpdateAdminInput: $UpdateAdminInput) {
    id
    sub
    email
    firstName
    lastName
    status
    createdAt
    updatedAt
    rank
  }
}
    `;

export const useUpdateAdminMutation = <
      TError = unknown,
      TContext = unknown
    >(options?: UseMutationOptions<operations.UpdateAdminMutation, TError, operations.UpdateAdminMutationVariables, TContext>) => {
    
    return useMutation<operations.UpdateAdminMutation, TError, operations.UpdateAdminMutationVariables, TContext>(
      {
    mutationKey: ['updateAdmin'],
    mutationFn: (variables?: operations.UpdateAdminMutationVariables) => graphqlFetcher<operations.UpdateAdminMutation, operations.UpdateAdminMutationVariables>(UpdateAdminDocument, variables)(),
    ...options
  }
    )};

export const FindClientByIdDocument = `
    query findClientById($GetByIdInput: GetByIdInput!) {
  findClientById(GetByIdInput: $GetByIdInput) {
    id
    is_organization_owner
    partner
    sub
    siret
    name
    address {
      id
      postalCode
      city
      address
      latitude
      longitude
      place_id
      country
    }
    numberOfVehiclesToDeliver
    contactFirstName
    contactLastName
    contactFunction
    contactService
    contactEmail
    contactPhone
    assigneeFirstName
    assigneeLastName
    assigneeFunction
    assigneeService
    assigneeEmail
    assigneePhone
    assigneeRank
    billingClerkFirstName
    billingClerkLastName
    billingClerkFunction
    billingClerkService
    billingClerkEmail
    billingClerkPhone
    createdAt
    updatedAt
  }
}
    `;

export const useFindClientByIdQuery = <
      TData = operations.FindClientByIdQuery,
      TError = unknown
    >(
      variables: operations.FindClientByIdQueryVariables,
      options?: Omit<UseQueryOptions<operations.FindClientByIdQuery, TError, TData>, 'queryKey'> & { queryKey?: UseQueryOptions<operations.FindClientByIdQuery, TError, TData>['queryKey'] }
    ) => {
    
    return useQuery<operations.FindClientByIdQuery, TError, TData>(
      {
    queryKey: ['findClientById', variables],
    queryFn: graphqlFetcher<operations.FindClientByIdQuery, operations.FindClientByIdQueryVariables>(FindClientByIdDocument, variables),
    ...options
  }
    )};

useFindClientByIdQuery.document = FindClientByIdDocument;

useFindClientByIdQuery.getKey = (variables: operations.FindClientByIdQueryVariables) => ['findClientById', variables];

export const FindClientBySubDocument = `
    query findClientBySub($GetBySubInput: GetBySubInput!) {
  findClientBySub(GetBySubInput: $GetBySubInput) {
    id
    is_organization_owner
    partner
    sub
    siret
    address {
      id
      postalCode
      city
      address
      latitude
      longitude
      place_id
      country
    }
    numberOfVehiclesToDeliver
    contactFirstName
    contactLastName
    contactFunction
    contactService
    contactEmail
    contactPhone
    assigneeFirstName
    assigneeLastName
    assigneeFunction
    assigneeService
    assigneeEmail
    assigneePhone
    assigneeRank
    clientOrganization {
      name
      slug
    }
    createdAt
    updatedAt
  }
}
    `;

export const useFindClientBySubQuery = <
      TData = operations.FindClientBySubQuery,
      TError = unknown
    >(
      variables: operations.FindClientBySubQueryVariables,
      options?: Omit<UseQueryOptions<operations.FindClientBySubQuery, TError, TData>, 'queryKey'> & { queryKey?: UseQueryOptions<operations.FindClientBySubQuery, TError, TData>['queryKey'] }
    ) => {
    
    return useQuery<operations.FindClientBySubQuery, TError, TData>(
      {
    queryKey: ['findClientBySub', variables],
    queryFn: graphqlFetcher<operations.FindClientBySubQuery, operations.FindClientBySubQueryVariables>(FindClientBySubDocument, variables),
    ...options
  }
    )};

useFindClientBySubQuery.document = FindClientBySubDocument;

useFindClientBySubQuery.getKey = (variables: operations.FindClientBySubQueryVariables) => ['findClientBySub', variables];

export const FindAllClientsDocument = `
    query findAllClients($GetAllInput: GetAllInput!) {
  findAllClients(GetAllInput: $GetAllInput) {
    total
    records {
      id
      is_organization_owner
      partner
      sub
      siret
      name
      address {
        id
        postalCode
        city
        address
      }
      numberOfVehiclesToDeliver
      contactFirstName
      contactLastName
      contactFunction
      contactService
      contactEmail
      contactPhone
      assigneeFirstName
      assigneeLastName
      assigneeEmail
      createdAt
    }
  }
}
    `;

export const useFindAllClientsQuery = <
      TData = operations.FindAllClientsQuery,
      TError = unknown
    >(
      variables: operations.FindAllClientsQueryVariables,
      options?: Omit<UseQueryOptions<operations.FindAllClientsQuery, TError, TData>, 'queryKey'> & { queryKey?: UseQueryOptions<operations.FindAllClientsQuery, TError, TData>['queryKey'] }
    ) => {
    
    return useQuery<operations.FindAllClientsQuery, TError, TData>(
      {
    queryKey: ['findAllClients', variables],
    queryFn: graphqlFetcher<operations.FindAllClientsQuery, operations.FindAllClientsQueryVariables>(FindAllClientsDocument, variables),
    ...options
  }
    )};

useFindAllClientsQuery.document = FindAllClientsDocument;

useFindAllClientsQuery.getKey = (variables: operations.FindAllClientsQueryVariables) => ['findAllClients', variables];

export const CreateClientDocument = `
    mutation createClient($CreateClientInput: CreateClientInput!) {
  createClient(CreateClientInput: $CreateClientInput) {
    id
    is_organization_owner
    partner
    sub
    siret
    name
    address {
      id
      postalCode
      city
      address
    }
    numberOfVehiclesToDeliver
    contactFirstName
    contactLastName
    contactFunction
    contactService
    contactEmail
    contactPhone
    assigneeFirstName
    assigneeLastName
    assigneeEmail
    assigneePhone
    createdAt
    updatedAt
  }
}
    `;

export const useCreateClientMutation = <
      TError = unknown,
      TContext = unknown
    >(options?: UseMutationOptions<operations.CreateClientMutation, TError, operations.CreateClientMutationVariables, TContext>) => {
    
    return useMutation<operations.CreateClientMutation, TError, operations.CreateClientMutationVariables, TContext>(
      {
    mutationKey: ['createClient'],
    mutationFn: (variables?: operations.CreateClientMutationVariables) => graphqlFetcher<operations.CreateClientMutation, operations.CreateClientMutationVariables>(CreateClientDocument, variables)(),
    ...options
  }
    )};

export const UpdateClientDocument = `
    mutation updateClient($UpdateClientInput: UpdateClientInput!) {
  updateClient(UpdateClientInput: $UpdateClientInput) {
    id
    is_organization_owner
    partner
    sub
    siret
    name
    address {
      id
      postalCode
      city
      address
    }
    numberOfVehiclesToDeliver
    contactFirstName
    contactLastName
    contactFunction
    contactService
    contactEmail
    contactPhone
    assigneeFirstName
    assigneeLastName
    assigneeEmail
    assigneePhone
    createdAt
    updatedAt
  }
}
    `;

export const useUpdateClientMutation = <
      TError = unknown,
      TContext = unknown
    >(options?: UseMutationOptions<operations.UpdateClientMutation, TError, operations.UpdateClientMutationVariables, TContext>) => {
    
    return useMutation<operations.UpdateClientMutation, TError, operations.UpdateClientMutationVariables, TContext>(
      {
    mutationKey: ['updateClient'],
    mutationFn: (variables?: operations.UpdateClientMutationVariables) => graphqlFetcher<operations.UpdateClientMutation, operations.UpdateClientMutationVariables>(UpdateClientDocument, variables)(),
    ...options
  }
    )};

export const DeleteClientDocument = `
    mutation deleteClient($DeleteInput: DeleteInput!) {
  deleteClient(DeleteInput: $DeleteInput) {
    id
  }
}
    `;

export const useDeleteClientMutation = <
      TError = unknown,
      TContext = unknown
    >(options?: UseMutationOptions<operations.DeleteClientMutation, TError, operations.DeleteClientMutationVariables, TContext>) => {
    
    return useMutation<operations.DeleteClientMutation, TError, operations.DeleteClientMutationVariables, TContext>(
      {
    mutationKey: ['deleteClient'],
    mutationFn: (variables?: operations.DeleteClientMutationVariables) => graphqlFetcher<operations.DeleteClientMutation, operations.DeleteClientMutationVariables>(DeleteClientDocument, variables)(),
    ...options
  }
    )};

export const FindDeliveryByIdDocument = `
    query findDeliveryById($GetByIdInput: GetByIdInput!) {
  findDeliveryById(GetByIdInput: $GetByIdInput) {
    id
    status
    type
    cancelledAt
    cancelReason
    smsSentAt
    destinationCode
    startCode
    invoice
    docs
    validationDocs
    carDeliveryDeadline
    wantedPrice
    numberOfVehicles
    number_of_offers
    duration
    distance
    minPrice
    maxPrice
    medianPrice
    clientId
    clientInitialPrice
    clientFinalPrice
    accepted_offer {
      demandedPrice
    }
    organization {
      slug
      name
    }
    ...EndPointSectionFields
    ...DeliveryAddressFields
    ...SummaryPartyFields
    ...SummaryAvailabilityFields
    ...SummaryDriverFields
    category
    comment
    selectedOptions {
      ...DeliveryOptionSelectionFields
    }
  }
}
    ${EndPointSectionFieldsFragmentDoc}
${VehicleSectionFieldsFragmentDoc}
${DeliveryAddressFieldsFragmentDoc}
${AddressFieldsFragmentDoc}
${SummaryPartyFieldsFragmentDoc}
${SummaryAvailabilityFieldsFragmentDoc}
${SummaryDriverFieldsFragmentDoc}
${DeliveryOptionSelectionFieldsFragmentDoc}`;

export const useFindDeliveryByIdQuery = <
      TData = operations.FindDeliveryByIdQuery,
      TError = unknown
    >(
      variables: operations.FindDeliveryByIdQueryVariables,
      options?: Omit<UseQueryOptions<operations.FindDeliveryByIdQuery, TError, TData>, 'queryKey'> & { queryKey?: UseQueryOptions<operations.FindDeliveryByIdQuery, TError, TData>['queryKey'] }
    ) => {
    
    return useQuery<operations.FindDeliveryByIdQuery, TError, TData>(
      {
    queryKey: ['findDeliveryById', variables],
    queryFn: graphqlFetcher<operations.FindDeliveryByIdQuery, operations.FindDeliveryByIdQueryVariables>(FindDeliveryByIdDocument, variables),
    ...options
  }
    )};

useFindDeliveryByIdQuery.document = FindDeliveryByIdDocument;

useFindDeliveryByIdQuery.getKey = (variables: operations.FindDeliveryByIdQueryVariables) => ['findDeliveryById', variables];

export const FindAllDeliveryDocument = `
    query findAllDelivery($GetAllInput: GetAllInput!) {
  findAllDelivery(GetAllInput: $GetAllInput) {
    total
    records {
      id
      publicId
      organizationId
      createdAt
      status
      type
      destinationCode
      carAvailabilityDate
      carDeliveryDeadline
      carPickUpDate
      departedAt
      completedAt
      wantedPrice
      numberOfVehicles
      number_of_offers
      duration
      distance
      comment
      confirmedAt
      smsSentAt
      remainingDistance
      totalHoursToEstimatedDate
      remainingHoursToEstimatedDate
      minPrice
      minOfferPrice
      maxPrice
      clientFirstName
      clientLastName
      clientPhone
      clientId
      clientCompany
      clientDrivingLicenceNumber
      clientInitialPrice
      averageOffersPrice
      clientFinalPrice
      finalPrice
      cancelledAt
      startCode
      invoice
      rated
      organization {
        slug
        name
      }
      selectedDriver {
        id
        firstName
        lastName
        avatar
        email
        fullName
      }
      fromAddress {
        id
        address
        addressPrecision
        city
        postalCode
        latitude
        longitude
      }
      toAddress {
        id
        address
        addressPrecision
        city
        postalCode
        latitude
        longitude
      }
      toReturnToAddress {
        id
        address
        addressPrecision
        city
        postalCode
        latitude
        longitude
      }
      vehicles {
        id
        type
        marque
        model
        vehicleCode
        vin
        carType
        toRetrieve
        isDelivered
      }
      accepted_offer {
        demandedPrice
      }
      selectedOptions {
        ...DeliveryOptionSelectionFields
      }
      docs
      subscriptions {
        id
        name
      }
    }
  }
}
    ${DeliveryOptionSelectionFieldsFragmentDoc}`;

export const useFindAllDeliveryQuery = <
      TData = operations.FindAllDeliveryQuery,
      TError = unknown
    >(
      variables: operations.FindAllDeliveryQueryVariables,
      options?: Omit<UseQueryOptions<operations.FindAllDeliveryQuery, TError, TData>, 'queryKey'> & { queryKey?: UseQueryOptions<operations.FindAllDeliveryQuery, TError, TData>['queryKey'] }
    ) => {
    
    return useQuery<operations.FindAllDeliveryQuery, TError, TData>(
      {
    queryKey: ['findAllDelivery', variables],
    queryFn: graphqlFetcher<operations.FindAllDeliveryQuery, operations.FindAllDeliveryQueryVariables>(FindAllDeliveryDocument, variables),
    ...options
  }
    )};

useFindAllDeliveryQuery.document = FindAllDeliveryDocument;

useFindAllDeliveryQuery.getKey = (variables: operations.FindAllDeliveryQueryVariables) => ['findAllDelivery', variables];

export const GetDeliveryStatsByTypeDocument = `
    query getDeliveryStatsByType {
  getDeliveryStatsByType {
    x
    y {
      STANDARD
      TO_RETURN
      TWO_WAY_DELIVERY
      OKM
    }
  }
}
    `;

export const useGetDeliveryStatsByTypeQuery = <
      TData = operations.GetDeliveryStatsByTypeQuery,
      TError = unknown
    >(
      variables?: operations.GetDeliveryStatsByTypeQueryVariables,
      options?: Omit<UseQueryOptions<operations.GetDeliveryStatsByTypeQuery, TError, TData>, 'queryKey'> & { queryKey?: UseQueryOptions<operations.GetDeliveryStatsByTypeQuery, TError, TData>['queryKey'] }
    ) => {
    
    return useQuery<operations.GetDeliveryStatsByTypeQuery, TError, TData>(
      {
    queryKey: variables === undefined ? ['getDeliveryStatsByType'] : ['getDeliveryStatsByType', variables],
    queryFn: graphqlFetcher<operations.GetDeliveryStatsByTypeQuery, operations.GetDeliveryStatsByTypeQueryVariables>(GetDeliveryStatsByTypeDocument, variables),
    ...options
  }
    )};

useGetDeliveryStatsByTypeQuery.document = GetDeliveryStatsByTypeDocument;

useGetDeliveryStatsByTypeQuery.getKey = (variables?: operations.GetDeliveryStatsByTypeQueryVariables) => variables === undefined ? ['getDeliveryStatsByType'] : ['getDeliveryStatsByType', variables];

export const GetDeliveryProgressStatsDocument = `
    query getDeliveryProgressStats($type: DeliveryType) {
  getDeliveryProgressStats(type: $type) {
    x
    y
  }
}
    `;

export const useGetDeliveryProgressStatsQuery = <
      TData = operations.GetDeliveryProgressStatsQuery,
      TError = unknown
    >(
      variables?: operations.GetDeliveryProgressStatsQueryVariables,
      options?: Omit<UseQueryOptions<operations.GetDeliveryProgressStatsQuery, TError, TData>, 'queryKey'> & { queryKey?: UseQueryOptions<operations.GetDeliveryProgressStatsQuery, TError, TData>['queryKey'] }
    ) => {
    
    return useQuery<operations.GetDeliveryProgressStatsQuery, TError, TData>(
      {
    queryKey: variables === undefined ? ['getDeliveryProgressStats'] : ['getDeliveryProgressStats', variables],
    queryFn: graphqlFetcher<operations.GetDeliveryProgressStatsQuery, operations.GetDeliveryProgressStatsQueryVariables>(GetDeliveryProgressStatsDocument, variables),
    ...options
  }
    )};

useGetDeliveryProgressStatsQuery.document = GetDeliveryProgressStatsDocument;

useGetDeliveryProgressStatsQuery.getKey = (variables?: operations.GetDeliveryProgressStatsQueryVariables) => variables === undefined ? ['getDeliveryProgressStats'] : ['getDeliveryProgressStats', variables];

export const GetDeliveryStatusStatsDocument = `
    query getDeliveryStatusStats {
  getDeliveryStatusStats {
    data {
      x
      y
    }
    total
  }
}
    `;

export const useGetDeliveryStatusStatsQuery = <
      TData = operations.GetDeliveryStatusStatsQuery,
      TError = unknown
    >(
      variables?: operations.GetDeliveryStatusStatsQueryVariables,
      options?: Omit<UseQueryOptions<operations.GetDeliveryStatusStatsQuery, TError, TData>, 'queryKey'> & { queryKey?: UseQueryOptions<operations.GetDeliveryStatusStatsQuery, TError, TData>['queryKey'] }
    ) => {
    
    return useQuery<operations.GetDeliveryStatusStatsQuery, TError, TData>(
      {
    queryKey: variables === undefined ? ['getDeliveryStatusStats'] : ['getDeliveryStatusStats', variables],
    queryFn: graphqlFetcher<operations.GetDeliveryStatusStatsQuery, operations.GetDeliveryStatusStatsQueryVariables>(GetDeliveryStatusStatsDocument, variables),
    ...options
  }
    )};

useGetDeliveryStatusStatsQuery.document = GetDeliveryStatusStatsDocument;

useGetDeliveryStatusStatsQuery.getKey = (variables?: operations.GetDeliveryStatusStatsQueryVariables) => variables === undefined ? ['getDeliveryStatusStats'] : ['getDeliveryStatusStats', variables];

export const CreateDeliveryDocument = `
    mutation createDelivery($createInput: CreateDeliveryInput!) {
  createDelivery(CreateDeliveryInput: $createInput) {
    id
    status
    type
    publicId
  }
}
    `;

export const useCreateDeliveryMutation = <
      TError = unknown,
      TContext = unknown
    >(options?: UseMutationOptions<operations.CreateDeliveryMutation, TError, operations.CreateDeliveryMutationVariables, TContext>) => {
    
    return useMutation<operations.CreateDeliveryMutation, TError, operations.CreateDeliveryMutationVariables, TContext>(
      {
    mutationKey: ['createDelivery'],
    mutationFn: (variables?: operations.CreateDeliveryMutationVariables) => graphqlFetcher<operations.CreateDeliveryMutation, operations.CreateDeliveryMutationVariables>(CreateDeliveryDocument, variables)(),
    ...options
  }
    )};

export const UpdateDeliveryDocument = `
    mutation updateDelivery($updateInput: UpdateDeliveryInput!) {
  updateDelivery(UpdateDeliveryInput: $updateInput) {
    id
    status
    type
    publicId
  }
}
    `;

export const useUpdateDeliveryMutation = <
      TError = unknown,
      TContext = unknown
    >(options?: UseMutationOptions<operations.UpdateDeliveryMutation, TError, operations.UpdateDeliveryMutationVariables, TContext>) => {
    
    return useMutation<operations.UpdateDeliveryMutation, TError, operations.UpdateDeliveryMutationVariables, TContext>(
      {
    mutationKey: ['updateDelivery'],
    mutationFn: (variables?: operations.UpdateDeliveryMutationVariables) => graphqlFetcher<operations.UpdateDeliveryMutation, operations.UpdateDeliveryMutationVariables>(UpdateDeliveryDocument, variables)(),
    ...options
  }
    )};

export const AllowDeliveryDocument = `
    mutation allowDelivery($AdminAllowDeliveryInput: AdminAllowDeliveryInput!) {
  allowDelivery(AdminAllowDeliveryInput: $AdminAllowDeliveryInput) {
    id
    status
    type
    publicId
  }
}
    `;

export const useAllowDeliveryMutation = <
      TError = unknown,
      TContext = unknown
    >(options?: UseMutationOptions<operations.AllowDeliveryMutation, TError, operations.AllowDeliveryMutationVariables, TContext>) => {
    
    return useMutation<operations.AllowDeliveryMutation, TError, operations.AllowDeliveryMutationVariables, TContext>(
      {
    mutationKey: ['allowDelivery'],
    mutationFn: (variables?: operations.AllowDeliveryMutationVariables) => graphqlFetcher<operations.AllowDeliveryMutation, operations.AllowDeliveryMutationVariables>(AllowDeliveryDocument, variables)(),
    ...options
  }
    )};

export const AssignDriverToDeliveryDocument = `
    mutation assignDriverToDelivery($UpdateDeliveryDriverInput: UpdateDeliveryDriverInput!) {
  assignDriverToDelivery(UpdateDeliveryDriverInput: $UpdateDeliveryDriverInput) {
    id
    status
    type
    publicId
  }
}
    `;

export const useAssignDriverToDeliveryMutation = <
      TError = unknown,
      TContext = unknown
    >(options?: UseMutationOptions<operations.AssignDriverToDeliveryMutation, TError, operations.AssignDriverToDeliveryMutationVariables, TContext>) => {
    
    return useMutation<operations.AssignDriverToDeliveryMutation, TError, operations.AssignDriverToDeliveryMutationVariables, TContext>(
      {
    mutationKey: ['assignDriverToDelivery'],
    mutationFn: (variables?: operations.AssignDriverToDeliveryMutationVariables) => graphqlFetcher<operations.AssignDriverToDeliveryMutation, operations.AssignDriverToDeliveryMutationVariables>(AssignDriverToDeliveryDocument, variables)(),
    ...options
  }
    )};

export const BulkDeleteDeliveriesDocument = `
    mutation BulkDeleteDeliveries($input: BulkDeleteDeliveriesInput!) {
  bulkDeleteDeliveries(BulkDeleteDeliveriesInput: $input) {
    successCount
    failures {
      deliveryId
      message
    }
  }
}
    `;

export const useBulkDeleteDeliveriesMutation = <
      TError = unknown,
      TContext = unknown
    >(options?: UseMutationOptions<operations.BulkDeleteDeliveriesMutation, TError, operations.BulkDeleteDeliveriesMutationVariables, TContext>) => {
    
    return useMutation<operations.BulkDeleteDeliveriesMutation, TError, operations.BulkDeleteDeliveriesMutationVariables, TContext>(
      {
    mutationKey: ['BulkDeleteDeliveries'],
    mutationFn: (variables?: operations.BulkDeleteDeliveriesMutationVariables) => graphqlFetcher<operations.BulkDeleteDeliveriesMutation, operations.BulkDeleteDeliveriesMutationVariables>(BulkDeleteDeliveriesDocument, variables)(),
    ...options
  }
    )};

export const CancelDeliveryDocument = `
    mutation CancelDelivery($CancelDeliveryInput: CancelDeliveryInput!) {
  cancelDelivery(CancelDeliveryInput: $CancelDeliveryInput) {
    id
    status
  }
}
    `;

export const useCancelDeliveryMutation = <
      TError = unknown,
      TContext = unknown
    >(options?: UseMutationOptions<operations.CancelDeliveryMutation, TError, operations.CancelDeliveryMutationVariables, TContext>) => {
    
    return useMutation<operations.CancelDeliveryMutation, TError, operations.CancelDeliveryMutationVariables, TContext>(
      {
    mutationKey: ['CancelDelivery'],
    mutationFn: (variables?: operations.CancelDeliveryMutationVariables) => graphqlFetcher<operations.CancelDeliveryMutation, operations.CancelDeliveryMutationVariables>(CancelDeliveryDocument, variables)(),
    ...options
  }
    )};

export const BulkCancelDeliveriesDocument = `
    mutation BulkCancelDeliveries($input: BulkCancelDeliveriesInput!) {
  bulkCancelDeliveries(BulkCancelDeliveriesInput: $input) {
    successCount
    failures {
      deliveryId
      message
    }
  }
}
    `;

export const useBulkCancelDeliveriesMutation = <
      TError = unknown,
      TContext = unknown
    >(options?: UseMutationOptions<operations.BulkCancelDeliveriesMutation, TError, operations.BulkCancelDeliveriesMutationVariables, TContext>) => {
    
    return useMutation<operations.BulkCancelDeliveriesMutation, TError, operations.BulkCancelDeliveriesMutationVariables, TContext>(
      {
    mutationKey: ['BulkCancelDeliveries'],
    mutationFn: (variables?: operations.BulkCancelDeliveriesMutationVariables) => graphqlFetcher<operations.BulkCancelDeliveriesMutation, operations.BulkCancelDeliveriesMutationVariables>(BulkCancelDeliveriesDocument, variables)(),
    ...options
  }
    )};

export const ConfirmDeliveryDocument = `
    mutation ConfirmDelivery($ConfirmDeliveryInput: ConfirmDeliveryInput!) {
  confirmDelivery(ConfirmDeliveryInput: $ConfirmDeliveryInput) {
    id
    status
  }
}
    `;

export const useConfirmDeliveryMutation = <
      TError = unknown,
      TContext = unknown
    >(options?: UseMutationOptions<operations.ConfirmDeliveryMutation, TError, operations.ConfirmDeliveryMutationVariables, TContext>) => {
    
    return useMutation<operations.ConfirmDeliveryMutation, TError, operations.ConfirmDeliveryMutationVariables, TContext>(
      {
    mutationKey: ['ConfirmDelivery'],
    mutationFn: (variables?: operations.ConfirmDeliveryMutationVariables) => graphqlFetcher<operations.ConfirmDeliveryMutation, operations.ConfirmDeliveryMutationVariables>(ConfirmDeliveryDocument, variables)(),
    ...options
  }
    )};

export const BulkConfirmDeliveriesDocument = `
    mutation BulkConfirmDeliveries($input: BulkConfirmDeliveriesInput!) {
  bulkConfirmDeliveries(BulkConfirmDeliveriesInput: $input) {
    successCount
    failures {
      deliveryId
      message
    }
  }
}
    `;

export const useBulkConfirmDeliveriesMutation = <
      TError = unknown,
      TContext = unknown
    >(options?: UseMutationOptions<operations.BulkConfirmDeliveriesMutation, TError, operations.BulkConfirmDeliveriesMutationVariables, TContext>) => {
    
    return useMutation<operations.BulkConfirmDeliveriesMutation, TError, operations.BulkConfirmDeliveriesMutationVariables, TContext>(
      {
    mutationKey: ['BulkConfirmDeliveries'],
    mutationFn: (variables?: operations.BulkConfirmDeliveriesMutationVariables) => graphqlFetcher<operations.BulkConfirmDeliveriesMutation, operations.BulkConfirmDeliveriesMutationVariables>(BulkConfirmDeliveriesDocument, variables)(),
    ...options
  }
    )};

export const UpdateDeliveryStatusDocument = `
    mutation UpdateDeliveryStatus($UpdateDeliveryStatusInput: updateDeliveryStatusInput!) {
  updateDeliveryStatus(UpdateDeliveryStatusInput: $UpdateDeliveryStatusInput) {
    id
    status
  }
}
    `;

export const useUpdateDeliveryStatusMutation = <
      TError = unknown,
      TContext = unknown
    >(options?: UseMutationOptions<operations.UpdateDeliveryStatusMutation, TError, operations.UpdateDeliveryStatusMutationVariables, TContext>) => {
    
    return useMutation<operations.UpdateDeliveryStatusMutation, TError, operations.UpdateDeliveryStatusMutationVariables, TContext>(
      {
    mutationKey: ['UpdateDeliveryStatus'],
    mutationFn: (variables?: operations.UpdateDeliveryStatusMutationVariables) => graphqlFetcher<operations.UpdateDeliveryStatusMutation, operations.UpdateDeliveryStatusMutationVariables>(UpdateDeliveryStatusDocument, variables)(),
    ...options
  }
    )};

export const UpdateDeliveryTypeDocument = `
    mutation UpdateDeliveryType($UpdateDeliveryTypeInput: updateDeliveryTypeInput!) {
  updateDeliveryType(UpdateDeliveryTypeInput: $UpdateDeliveryTypeInput) {
    id
    type
  }
}
    `;

export const useUpdateDeliveryTypeMutation = <
      TError = unknown,
      TContext = unknown
    >(options?: UseMutationOptions<operations.UpdateDeliveryTypeMutation, TError, operations.UpdateDeliveryTypeMutationVariables, TContext>) => {
    
    return useMutation<operations.UpdateDeliveryTypeMutation, TError, operations.UpdateDeliveryTypeMutationVariables, TContext>(
      {
    mutationKey: ['UpdateDeliveryType'],
    mutationFn: (variables?: operations.UpdateDeliveryTypeMutationVariables) => graphqlFetcher<operations.UpdateDeliveryTypeMutation, operations.UpdateDeliveryTypeMutationVariables>(UpdateDeliveryTypeDocument, variables)(),
    ...options
  }
    )};

export const GetDeliveriesCountByAdminDocument = `
    query GetDeliveriesCountByAdmin {
  getDeliveriesCountByAdmin {
    requestedCount
    confirmedCount
    onGoingCount
    waitingDriverCount
    setCount
    alertCount
    cancelledCount
    deliveredCount
  }
}
    `;

export const useGetDeliveriesCountByAdminQuery = <
      TData = operations.GetDeliveriesCountByAdminQuery,
      TError = unknown
    >(
      variables?: operations.GetDeliveriesCountByAdminQueryVariables,
      options?: Omit<UseQueryOptions<operations.GetDeliveriesCountByAdminQuery, TError, TData>, 'queryKey'> & { queryKey?: UseQueryOptions<operations.GetDeliveriesCountByAdminQuery, TError, TData>['queryKey'] }
    ) => {
    
    return useQuery<operations.GetDeliveriesCountByAdminQuery, TError, TData>(
      {
    queryKey: variables === undefined ? ['GetDeliveriesCountByAdmin'] : ['GetDeliveriesCountByAdmin', variables],
    queryFn: graphqlFetcher<operations.GetDeliveriesCountByAdminQuery, operations.GetDeliveriesCountByAdminQueryVariables>(GetDeliveriesCountByAdminDocument, variables),
    ...options
  }
    )};

useGetDeliveriesCountByAdminQuery.document = GetDeliveriesCountByAdminDocument;

useGetDeliveriesCountByAdminQuery.getKey = (variables?: operations.GetDeliveriesCountByAdminQueryVariables) => variables === undefined ? ['GetDeliveriesCountByAdmin'] : ['GetDeliveriesCountByAdmin', variables];

export const GetDeliveriesCountByClientDocument = `
    query GetDeliveriesCountByClient {
  getDeliveriesCountByClient {
    requestedCount
    confirmedCount
    onGoingCount
    cancelledCount
    deliveredCount
  }
}
    `;

export const useGetDeliveriesCountByClientQuery = <
      TData = operations.GetDeliveriesCountByClientQuery,
      TError = unknown
    >(
      variables?: operations.GetDeliveriesCountByClientQueryVariables,
      options?: Omit<UseQueryOptions<operations.GetDeliveriesCountByClientQuery, TError, TData>, 'queryKey'> & { queryKey?: UseQueryOptions<operations.GetDeliveriesCountByClientQuery, TError, TData>['queryKey'] }
    ) => {
    
    return useQuery<operations.GetDeliveriesCountByClientQuery, TError, TData>(
      {
    queryKey: variables === undefined ? ['GetDeliveriesCountByClient'] : ['GetDeliveriesCountByClient', variables],
    queryFn: graphqlFetcher<operations.GetDeliveriesCountByClientQuery, operations.GetDeliveriesCountByClientQueryVariables>(GetDeliveriesCountByClientDocument, variables),
    ...options
  }
    )};

useGetDeliveriesCountByClientQuery.document = GetDeliveriesCountByClientDocument;

useGetDeliveriesCountByClientQuery.getKey = (variables?: operations.GetDeliveriesCountByClientQueryVariables) => variables === undefined ? ['GetDeliveriesCountByClient'] : ['GetDeliveriesCountByClient', variables];

export const FindDeliveryLogsDocument = `
    query FindDeliveryLogs($deliveryId: Int!, $page: Int, $pageSize: Int) {
  findDeliveryLogs(deliveryId: $deliveryId, page: $page, pageSize: $pageSize) {
    totalCount
    logs {
      id
      log
      createdAt
      deliveryId
    }
  }
}
    `;

export const useFindDeliveryLogsQuery = <
      TData = operations.FindDeliveryLogsQuery,
      TError = unknown
    >(
      variables: operations.FindDeliveryLogsQueryVariables,
      options?: Omit<UseQueryOptions<operations.FindDeliveryLogsQuery, TError, TData>, 'queryKey'> & { queryKey?: UseQueryOptions<operations.FindDeliveryLogsQuery, TError, TData>['queryKey'] }
    ) => {
    
    return useQuery<operations.FindDeliveryLogsQuery, TError, TData>(
      {
    queryKey: ['FindDeliveryLogs', variables],
    queryFn: graphqlFetcher<operations.FindDeliveryLogsQuery, operations.FindDeliveryLogsQueryVariables>(FindDeliveryLogsDocument, variables),
    ...options
  }
    )};

useFindDeliveryLogsQuery.document = FindDeliveryLogsDocument;

useFindDeliveryLogsQuery.getKey = (variables: operations.FindDeliveryLogsQueryVariables) => ['FindDeliveryLogs', variables];

export const AddDeliveryOptionToDeliveryDocument = `
    mutation addDeliveryOptionToDelivery($addInput: AddDeliveryOptionToDeliveryInput!) {
  addDeliveryOptionToDelivery(addInput: $addInput) {
    ...DeliveryOptionSelectionFields
  }
}
    ${DeliveryOptionSelectionFieldsFragmentDoc}`;

export const useAddDeliveryOptionToDeliveryMutation = <
      TError = unknown,
      TContext = unknown
    >(options?: UseMutationOptions<operations.AddDeliveryOptionToDeliveryMutation, TError, operations.AddDeliveryOptionToDeliveryMutationVariables, TContext>) => {
    
    return useMutation<operations.AddDeliveryOptionToDeliveryMutation, TError, operations.AddDeliveryOptionToDeliveryMutationVariables, TContext>(
      {
    mutationKey: ['addDeliveryOptionToDelivery'],
    mutationFn: (variables?: operations.AddDeliveryOptionToDeliveryMutationVariables) => graphqlFetcher<operations.AddDeliveryOptionToDeliveryMutation, operations.AddDeliveryOptionToDeliveryMutationVariables>(AddDeliveryOptionToDeliveryDocument, variables)(),
    ...options
  }
    )};

export const UpdateDeliveryOptionSelectionDocument = `
    mutation updateDeliveryOptionSelection($updateInput: UpdateDeliveryOptionSelectionInput!) {
  updateDeliveryOptionSelection(updateInput: $updateInput) {
    ...DeliveryOptionSelectionFields
  }
}
    ${DeliveryOptionSelectionFieldsFragmentDoc}`;

export const useUpdateDeliveryOptionSelectionMutation = <
      TError = unknown,
      TContext = unknown
    >(options?: UseMutationOptions<operations.UpdateDeliveryOptionSelectionMutation, TError, operations.UpdateDeliveryOptionSelectionMutationVariables, TContext>) => {
    
    return useMutation<operations.UpdateDeliveryOptionSelectionMutation, TError, operations.UpdateDeliveryOptionSelectionMutationVariables, TContext>(
      {
    mutationKey: ['updateDeliveryOptionSelection'],
    mutationFn: (variables?: operations.UpdateDeliveryOptionSelectionMutationVariables) => graphqlFetcher<operations.UpdateDeliveryOptionSelectionMutation, operations.UpdateDeliveryOptionSelectionMutationVariables>(UpdateDeliveryOptionSelectionDocument, variables)(),
    ...options
  }
    )};

export const DeleteDeliveryDocument = `
    mutation deleteDelivery($DeleteInput: DeleteInput!) {
  deleteDelivery(DeleteInput: $DeleteInput) {
    id
  }
}
    `;

export const useDeleteDeliveryMutation = <
      TError = unknown,
      TContext = unknown
    >(options?: UseMutationOptions<operations.DeleteDeliveryMutation, TError, operations.DeleteDeliveryMutationVariables, TContext>) => {
    
    return useMutation<operations.DeleteDeliveryMutation, TError, operations.DeleteDeliveryMutationVariables, TContext>(
      {
    mutationKey: ['deleteDelivery'],
    mutationFn: (variables?: operations.DeleteDeliveryMutationVariables) => graphqlFetcher<operations.DeleteDeliveryMutation, operations.DeleteDeliveryMutationVariables>(DeleteDeliveryDocument, variables)(),
    ...options
  }
    )};

export const DeleteManyDeliveriesDocument = `
    mutation deleteManyDeliveries($DeleteManyInput: DeleteManyInput!) {
  deleteManyDeliveries(DeleteManyInput: $DeleteManyInput)
}
    `;

export const useDeleteManyDeliveriesMutation = <
      TError = unknown,
      TContext = unknown
    >(options?: UseMutationOptions<operations.DeleteManyDeliveriesMutation, TError, operations.DeleteManyDeliveriesMutationVariables, TContext>) => {
    
    return useMutation<operations.DeleteManyDeliveriesMutation, TError, operations.DeleteManyDeliveriesMutationVariables, TContext>(
      {
    mutationKey: ['deleteManyDeliveries'],
    mutationFn: (variables?: operations.DeleteManyDeliveriesMutationVariables) => graphqlFetcher<operations.DeleteManyDeliveriesMutation, operations.DeleteManyDeliveriesMutationVariables>(DeleteManyDeliveriesDocument, variables)(),
    ...options
  }
    )};

export const FindDeliveryAndSubscriptionsDocument = `
    query findDeliveryAndSubscriptions($GetByIdInput: GetByIdInput!) {
  findDeliveryAndSubscriptions(GetByIdInput: $GetByIdInput) {
    delivery {
      id
      publicId
      carAvailabilityDate
      carPickUpDate
      carDeliveryDeadline
      representativeCompany
      numberOfVehicles
      distance
      duration
      selectedOptions {
        ...DeliveryOptionSelectionFields
      }
      clientInitialPrice
      clientFinalPrice
      minPrice
      medianPrice
      maxPrice
      type
      fromAddress {
        postalCode
        city
        longitude
        latitude
        isInIdf
        address
      }
      toAddress {
        city
        postalCode
        latitude
        isInIdf
        longitude
        address
      }
      client {
        name
      }
      subscriptions {
        id
        name
      }
    }
    subscriptions {
      id
      name
    }
  }
}
    ${DeliveryOptionSelectionFieldsFragmentDoc}`;

export const useFindDeliveryAndSubscriptionsQuery = <
      TData = operations.FindDeliveryAndSubscriptionsQuery,
      TError = unknown
    >(
      variables: operations.FindDeliveryAndSubscriptionsQueryVariables,
      options?: Omit<UseQueryOptions<operations.FindDeliveryAndSubscriptionsQuery, TError, TData>, 'queryKey'> & { queryKey?: UseQueryOptions<operations.FindDeliveryAndSubscriptionsQuery, TError, TData>['queryKey'] }
    ) => {
    
    return useQuery<operations.FindDeliveryAndSubscriptionsQuery, TError, TData>(
      {
    queryKey: ['findDeliveryAndSubscriptions', variables],
    queryFn: graphqlFetcher<operations.FindDeliveryAndSubscriptionsQuery, operations.FindDeliveryAndSubscriptionsQueryVariables>(FindDeliveryAndSubscriptionsDocument, variables),
    ...options
  }
    )};

useFindDeliveryAndSubscriptionsQuery.document = FindDeliveryAndSubscriptionsDocument;

useFindDeliveryAndSubscriptionsQuery.getKey = (variables: operations.FindDeliveryAndSubscriptionsQueryVariables) => ['findDeliveryAndSubscriptions', variables];

export const FindAllDeliveryOptionsDocument = `
    query FindAllDeliveryOptions {
  findAllDeliveryOptions {
    ...DeliveryOptionFields
    partnerAssignments {
      ...DeliveryOptionOrganizationFields
    }
  }
}
    ${DeliveryOptionFieldsFragmentDoc}
${DeliveryOptionOrganizationFieldsFragmentDoc}`;

export const useFindAllDeliveryOptionsQuery = <
      TData = operations.FindAllDeliveryOptionsQuery,
      TError = unknown
    >(
      variables?: operations.FindAllDeliveryOptionsQueryVariables,
      options?: Omit<UseQueryOptions<operations.FindAllDeliveryOptionsQuery, TError, TData>, 'queryKey'> & { queryKey?: UseQueryOptions<operations.FindAllDeliveryOptionsQuery, TError, TData>['queryKey'] }
    ) => {
    
    return useQuery<operations.FindAllDeliveryOptionsQuery, TError, TData>(
      {
    queryKey: variables === undefined ? ['FindAllDeliveryOptions'] : ['FindAllDeliveryOptions', variables],
    queryFn: graphqlFetcher<operations.FindAllDeliveryOptionsQuery, operations.FindAllDeliveryOptionsQueryVariables>(FindAllDeliveryOptionsDocument, variables),
    ...options
  }
    )};

useFindAllDeliveryOptionsQuery.document = FindAllDeliveryOptionsDocument;

useFindAllDeliveryOptionsQuery.getKey = (variables?: operations.FindAllDeliveryOptionsQueryVariables) => variables === undefined ? ['FindAllDeliveryOptions'] : ['FindAllDeliveryOptions', variables];

export const FindDeliveryOptionDocument = `
    query FindDeliveryOption($id: Int!) {
  findDeliveryOption(id: $id) {
    ...DeliveryOptionFields
    partnerAssignments {
      ...DeliveryOptionOrganizationFields
    }
  }
}
    ${DeliveryOptionFieldsFragmentDoc}
${DeliveryOptionOrganizationFieldsFragmentDoc}`;

export const useFindDeliveryOptionQuery = <
      TData = operations.FindDeliveryOptionQuery,
      TError = unknown
    >(
      variables: operations.FindDeliveryOptionQueryVariables,
      options?: Omit<UseQueryOptions<operations.FindDeliveryOptionQuery, TError, TData>, 'queryKey'> & { queryKey?: UseQueryOptions<operations.FindDeliveryOptionQuery, TError, TData>['queryKey'] }
    ) => {
    
    return useQuery<operations.FindDeliveryOptionQuery, TError, TData>(
      {
    queryKey: ['FindDeliveryOption', variables],
    queryFn: graphqlFetcher<operations.FindDeliveryOptionQuery, operations.FindDeliveryOptionQueryVariables>(FindDeliveryOptionDocument, variables),
    ...options
  }
    )};

useFindDeliveryOptionQuery.document = FindDeliveryOptionDocument;

useFindDeliveryOptionQuery.getKey = (variables: operations.FindDeliveryOptionQueryVariables) => ['FindDeliveryOption', variables];

export const FindDeliveryOptionsForPartnerDocument = `
    query FindDeliveryOptionsForPartner($organizationId: String!) {
  findDeliveryOptionsForPartner(organizationId: $organizationId) {
    ...ResolvedDeliveryOptionFields
  }
}
    ${ResolvedDeliveryOptionFieldsFragmentDoc}`;

export const useFindDeliveryOptionsForPartnerQuery = <
      TData = operations.FindDeliveryOptionsForPartnerQuery,
      TError = unknown
    >(
      variables: operations.FindDeliveryOptionsForPartnerQueryVariables,
      options?: Omit<UseQueryOptions<operations.FindDeliveryOptionsForPartnerQuery, TError, TData>, 'queryKey'> & { queryKey?: UseQueryOptions<operations.FindDeliveryOptionsForPartnerQuery, TError, TData>['queryKey'] }
    ) => {
    
    return useQuery<operations.FindDeliveryOptionsForPartnerQuery, TError, TData>(
      {
    queryKey: ['FindDeliveryOptionsForPartner', variables],
    queryFn: graphqlFetcher<operations.FindDeliveryOptionsForPartnerQuery, operations.FindDeliveryOptionsForPartnerQueryVariables>(FindDeliveryOptionsForPartnerDocument, variables),
    ...options
  }
    )};

useFindDeliveryOptionsForPartnerQuery.document = FindDeliveryOptionsForPartnerDocument;

useFindDeliveryOptionsForPartnerQuery.getKey = (variables: operations.FindDeliveryOptionsForPartnerQueryVariables) => ['FindDeliveryOptionsForPartner', variables];

export const FindDeliveryOptionPartnerAssignmentsDocument = `
    query FindDeliveryOptionPartnerAssignments($optionId: Int!) {
  findDeliveryOptionPartnerAssignments(optionId: $optionId) {
    ...DeliveryOptionAssignmentEnrichedFields
  }
}
    ${DeliveryOptionAssignmentEnrichedFieldsFragmentDoc}`;

export const useFindDeliveryOptionPartnerAssignmentsQuery = <
      TData = operations.FindDeliveryOptionPartnerAssignmentsQuery,
      TError = unknown
    >(
      variables: operations.FindDeliveryOptionPartnerAssignmentsQueryVariables,
      options?: Omit<UseQueryOptions<operations.FindDeliveryOptionPartnerAssignmentsQuery, TError, TData>, 'queryKey'> & { queryKey?: UseQueryOptions<operations.FindDeliveryOptionPartnerAssignmentsQuery, TError, TData>['queryKey'] }
    ) => {
    
    return useQuery<operations.FindDeliveryOptionPartnerAssignmentsQuery, TError, TData>(
      {
    queryKey: ['FindDeliveryOptionPartnerAssignments', variables],
    queryFn: graphqlFetcher<operations.FindDeliveryOptionPartnerAssignmentsQuery, operations.FindDeliveryOptionPartnerAssignmentsQueryVariables>(FindDeliveryOptionPartnerAssignmentsDocument, variables),
    ...options
  }
    )};

useFindDeliveryOptionPartnerAssignmentsQuery.document = FindDeliveryOptionPartnerAssignmentsDocument;

useFindDeliveryOptionPartnerAssignmentsQuery.getKey = (variables: operations.FindDeliveryOptionPartnerAssignmentsQueryVariables) => ['FindDeliveryOptionPartnerAssignments', variables];

export const CreateDeliveryOptionDocument = `
    mutation CreateDeliveryOption($createInput: CreateDeliveryOptionInput!) {
  createDeliveryOption(createInput: $createInput) {
    ...DeliveryOptionFields
  }
}
    ${DeliveryOptionFieldsFragmentDoc}`;

export const useCreateDeliveryOptionMutation = <
      TError = unknown,
      TContext = unknown
    >(options?: UseMutationOptions<operations.CreateDeliveryOptionMutation, TError, operations.CreateDeliveryOptionMutationVariables, TContext>) => {
    
    return useMutation<operations.CreateDeliveryOptionMutation, TError, operations.CreateDeliveryOptionMutationVariables, TContext>(
      {
    mutationKey: ['CreateDeliveryOption'],
    mutationFn: (variables?: operations.CreateDeliveryOptionMutationVariables) => graphqlFetcher<operations.CreateDeliveryOptionMutation, operations.CreateDeliveryOptionMutationVariables>(CreateDeliveryOptionDocument, variables)(),
    ...options
  }
    )};

export const UpdateDeliveryOptionDocument = `
    mutation UpdateDeliveryOption($updateInput: UpdateDeliveryOptionInput!) {
  updateDeliveryOption(updateInput: $updateInput) {
    ...DeliveryOptionFields
  }
}
    ${DeliveryOptionFieldsFragmentDoc}`;

export const useUpdateDeliveryOptionMutation = <
      TError = unknown,
      TContext = unknown
    >(options?: UseMutationOptions<operations.UpdateDeliveryOptionMutation, TError, operations.UpdateDeliveryOptionMutationVariables, TContext>) => {
    
    return useMutation<operations.UpdateDeliveryOptionMutation, TError, operations.UpdateDeliveryOptionMutationVariables, TContext>(
      {
    mutationKey: ['UpdateDeliveryOption'],
    mutationFn: (variables?: operations.UpdateDeliveryOptionMutationVariables) => graphqlFetcher<operations.UpdateDeliveryOptionMutation, operations.UpdateDeliveryOptionMutationVariables>(UpdateDeliveryOptionDocument, variables)(),
    ...options
  }
    )};

export const DeactivateDeliveryOptionDocument = `
    mutation DeactivateDeliveryOption($id: Int!) {
  deactivateDeliveryOption(id: $id) {
    ...DeliveryOptionFields
  }
}
    ${DeliveryOptionFieldsFragmentDoc}`;

export const useDeactivateDeliveryOptionMutation = <
      TError = unknown,
      TContext = unknown
    >(options?: UseMutationOptions<operations.DeactivateDeliveryOptionMutation, TError, operations.DeactivateDeliveryOptionMutationVariables, TContext>) => {
    
    return useMutation<operations.DeactivateDeliveryOptionMutation, TError, operations.DeactivateDeliveryOptionMutationVariables, TContext>(
      {
    mutationKey: ['DeactivateDeliveryOption'],
    mutationFn: (variables?: operations.DeactivateDeliveryOptionMutationVariables) => graphqlFetcher<operations.DeactivateDeliveryOptionMutation, operations.DeactivateDeliveryOptionMutationVariables>(DeactivateDeliveryOptionDocument, variables)(),
    ...options
  }
    )};

export const DeleteDeliveryOptionDocument = `
    mutation DeleteDeliveryOption($id: Int!) {
  deleteDeliveryOption(id: $id)
}
    `;

export const useDeleteDeliveryOptionMutation = <
      TError = unknown,
      TContext = unknown
    >(options?: UseMutationOptions<operations.DeleteDeliveryOptionMutation, TError, operations.DeleteDeliveryOptionMutationVariables, TContext>) => {
    
    return useMutation<operations.DeleteDeliveryOptionMutation, TError, operations.DeleteDeliveryOptionMutationVariables, TContext>(
      {
    mutationKey: ['DeleteDeliveryOption'],
    mutationFn: (variables?: operations.DeleteDeliveryOptionMutationVariables) => graphqlFetcher<operations.DeleteDeliveryOptionMutation, operations.DeleteDeliveryOptionMutationVariables>(DeleteDeliveryOptionDocument, variables)(),
    ...options
  }
    )};

export const UpsertDeliveryOptionOrganizationDocument = `
    mutation UpsertDeliveryOptionOrganization($upsertInput: UpsertDeliveryOptionOrganizationInput!) {
  upsertDeliveryOptionOrganization(upsertInput: $upsertInput) {
    ...DeliveryOptionOrganizationFields
  }
}
    ${DeliveryOptionOrganizationFieldsFragmentDoc}`;

export const useUpsertDeliveryOptionOrganizationMutation = <
      TError = unknown,
      TContext = unknown
    >(options?: UseMutationOptions<operations.UpsertDeliveryOptionOrganizationMutation, TError, operations.UpsertDeliveryOptionOrganizationMutationVariables, TContext>) => {
    
    return useMutation<operations.UpsertDeliveryOptionOrganizationMutation, TError, operations.UpsertDeliveryOptionOrganizationMutationVariables, TContext>(
      {
    mutationKey: ['UpsertDeliveryOptionOrganization'],
    mutationFn: (variables?: operations.UpsertDeliveryOptionOrganizationMutationVariables) => graphqlFetcher<operations.UpsertDeliveryOptionOrganizationMutation, operations.UpsertDeliveryOptionOrganizationMutationVariables>(UpsertDeliveryOptionOrganizationDocument, variables)(),
    ...options
  }
    )};

export const SetDeliveryOptionPartnerAssignmentsDocument = `
    mutation SetDeliveryOptionPartnerAssignments($setInput: SetDeliveryOptionPartnerAssignmentsInput!) {
  setDeliveryOptionPartnerAssignments(setInput: $setInput) {
    ...DeliveryOptionAssignmentEnrichedFields
  }
}
    ${DeliveryOptionAssignmentEnrichedFieldsFragmentDoc}`;

export const useSetDeliveryOptionPartnerAssignmentsMutation = <
      TError = unknown,
      TContext = unknown
    >(options?: UseMutationOptions<operations.SetDeliveryOptionPartnerAssignmentsMutation, TError, operations.SetDeliveryOptionPartnerAssignmentsMutationVariables, TContext>) => {
    
    return useMutation<operations.SetDeliveryOptionPartnerAssignmentsMutation, TError, operations.SetDeliveryOptionPartnerAssignmentsMutationVariables, TContext>(
      {
    mutationKey: ['SetDeliveryOptionPartnerAssignments'],
    mutationFn: (variables?: operations.SetDeliveryOptionPartnerAssignmentsMutationVariables) => graphqlFetcher<operations.SetDeliveryOptionPartnerAssignmentsMutation, operations.SetDeliveryOptionPartnerAssignmentsMutationVariables>(SetDeliveryOptionPartnerAssignmentsDocument, variables)(),
    ...options
  }
    )};

export const RemoveDeliveryOptionPartnerAssignmentDocument = `
    mutation RemoveDeliveryOptionPartnerAssignment($optionId: Int!, $organizationId: String!) {
  removeDeliveryOptionPartnerAssignment(
    optionId: $optionId
    organizationId: $organizationId
  )
}
    `;

export const useRemoveDeliveryOptionPartnerAssignmentMutation = <
      TError = unknown,
      TContext = unknown
    >(options?: UseMutationOptions<operations.RemoveDeliveryOptionPartnerAssignmentMutation, TError, operations.RemoveDeliveryOptionPartnerAssignmentMutationVariables, TContext>) => {
    
    return useMutation<operations.RemoveDeliveryOptionPartnerAssignmentMutation, TError, operations.RemoveDeliveryOptionPartnerAssignmentMutationVariables, TContext>(
      {
    mutationKey: ['RemoveDeliveryOptionPartnerAssignment'],
    mutationFn: (variables?: operations.RemoveDeliveryOptionPartnerAssignmentMutationVariables) => graphqlFetcher<operations.RemoveDeliveryOptionPartnerAssignmentMutation, operations.RemoveDeliveryOptionPartnerAssignmentMutationVariables>(RemoveDeliveryOptionPartnerAssignmentDocument, variables)(),
    ...options
  }
    )};

export const GetDriverByIdDocument = `
    query getDriverById($GetByIdInput: GetByIdInput!) {
  getDriverById(GetByIdInput: $GetByIdInput) {
    id
    confirmedByAlb
    disabled
    firstName
    lastName
    email
    birthDate
    docs
    videoPath
    phoneNumber
    address {
      id
      postalCode
      city
      address
      addressPrecision
    }
    drivingLicenseDate
    drivingPermitNumber
    drivingPermitDueDate
    drivingLicensePath
    drivingLicensePath2
    idPath
    idPath2
    kbisPath
    siret
    freelancer
    company
    mcqAnswers
    mcqScore
    iban
    ribPath
    permit_solde_path
    truck_ownership_status
    wgarage
    insurance_expiration_date
    insurance_document_path
    vigilance_issue_date
    vigilance_certificate_path
    kbis_issue_date
    business_status
    driverType
    nbDeals
    lossDeals
    stokeDeals
    takeOffRating
    outfitRating
    qualityRating
    correspondenceRating
    justiceRating
    totalRating
    driverOffersPrice
    driverAttentePrice
    driverAverageBills
    specificNote
    subdivision
    activeDays
    subscription {
      name
      price
    }
  }
}
    `;

export const useGetDriverByIdQuery = <
      TData = operations.GetDriverByIdQuery,
      TError = unknown
    >(
      variables: operations.GetDriverByIdQueryVariables,
      options?: Omit<UseQueryOptions<operations.GetDriverByIdQuery, TError, TData>, 'queryKey'> & { queryKey?: UseQueryOptions<operations.GetDriverByIdQuery, TError, TData>['queryKey'] }
    ) => {
    
    return useQuery<operations.GetDriverByIdQuery, TError, TData>(
      {
    queryKey: ['getDriverById', variables],
    queryFn: graphqlFetcher<operations.GetDriverByIdQuery, operations.GetDriverByIdQueryVariables>(GetDriverByIdDocument, variables),
    ...options
  }
    )};

useGetDriverByIdQuery.document = GetDriverByIdDocument;

useGetDriverByIdQuery.getKey = (variables: operations.GetDriverByIdQueryVariables) => ['getDriverById', variables];

export const UpdateDriverDocument = `
    mutation UpdateDriver($input: UpdateDriverInput!) {
  updateDriver(UpdateDriverInput: $input) {
    id
    firstName
    lastName
    email
    phoneNumber
    birthDate
    drivingLicenseDate
    docs
    drivingPermitNumber
    drivingPermitDueDate
    business_status
    specificNote
    iban
    registrationStep
    address {
      id
      city
    }
  }
}
    `;

export const useUpdateDriverMutation = <
      TError = unknown,
      TContext = unknown
    >(options?: UseMutationOptions<operations.UpdateDriverMutation, TError, operations.UpdateDriverMutationVariables, TContext>) => {
    
    return useMutation<operations.UpdateDriverMutation, TError, operations.UpdateDriverMutationVariables, TContext>(
      {
    mutationKey: ['UpdateDriver'],
    mutationFn: (variables?: operations.UpdateDriverMutationVariables) => graphqlFetcher<operations.UpdateDriverMutation, operations.UpdateDriverMutationVariables>(UpdateDriverDocument, variables)(),
    ...options
  }
    )};

export const ConfirmDriverDocument = `
    mutation ConfirmDriver($input: ConfirmDriverRequestInput!) {
  confirmDriverByAlbExpress(input: $input)
}
    `;

export const useConfirmDriverMutation = <
      TError = unknown,
      TContext = unknown
    >(options?: UseMutationOptions<operations.ConfirmDriverMutation, TError, operations.ConfirmDriverMutationVariables, TContext>) => {
    
    return useMutation<operations.ConfirmDriverMutation, TError, operations.ConfirmDriverMutationVariables, TContext>(
      {
    mutationKey: ['ConfirmDriver'],
    mutationFn: (variables?: operations.ConfirmDriverMutationVariables) => graphqlFetcher<operations.ConfirmDriverMutation, operations.ConfirmDriverMutationVariables>(ConfirmDriverDocument, variables)(),
    ...options
  }
    )};

export const RejectDriverRequestDocument = `
    mutation RejectDriverRequest($input: DisableUserInput!) {
  rejectDriverRequest(input: $input)
}
    `;

export const useRejectDriverRequestMutation = <
      TError = unknown,
      TContext = unknown
    >(options?: UseMutationOptions<operations.RejectDriverRequestMutation, TError, operations.RejectDriverRequestMutationVariables, TContext>) => {
    
    return useMutation<operations.RejectDriverRequestMutation, TError, operations.RejectDriverRequestMutationVariables, TContext>(
      {
    mutationKey: ['RejectDriverRequest'],
    mutationFn: (variables?: operations.RejectDriverRequestMutationVariables) => graphqlFetcher<operations.RejectDriverRequestMutation, operations.RejectDriverRequestMutationVariables>(RejectDriverRequestDocument, variables)(),
    ...options
  }
    )};

export const CancelRejectDriverRequestDocument = `
    mutation CancelRejectDriverRequest($input: DisableUserInput!) {
  cancelRejectDriverRequest(input: $input)
}
    `;

export const useCancelRejectDriverRequestMutation = <
      TError = unknown,
      TContext = unknown
    >(options?: UseMutationOptions<operations.CancelRejectDriverRequestMutation, TError, operations.CancelRejectDriverRequestMutationVariables, TContext>) => {
    
    return useMutation<operations.CancelRejectDriverRequestMutation, TError, operations.CancelRejectDriverRequestMutationVariables, TContext>(
      {
    mutationKey: ['CancelRejectDriverRequest'],
    mutationFn: (variables?: operations.CancelRejectDriverRequestMutationVariables) => graphqlFetcher<operations.CancelRejectDriverRequestMutation, operations.CancelRejectDriverRequestMutationVariables>(CancelRejectDriverRequestDocument, variables)(),
    ...options
  }
    )};

export const DisableDriverDocument = `
    mutation DisableDriver($input: DisableUserInput!) {
  disableDriver(input: $input)
}
    `;

export const useDisableDriverMutation = <
      TError = unknown,
      TContext = unknown
    >(options?: UseMutationOptions<operations.DisableDriverMutation, TError, operations.DisableDriverMutationVariables, TContext>) => {
    
    return useMutation<operations.DisableDriverMutation, TError, operations.DisableDriverMutationVariables, TContext>(
      {
    mutationKey: ['DisableDriver'],
    mutationFn: (variables?: operations.DisableDriverMutationVariables) => graphqlFetcher<operations.DisableDriverMutation, operations.DisableDriverMutationVariables>(DisableDriverDocument, variables)(),
    ...options
  }
    )};

export const EnableDriverDocument = `
    mutation EnableDriver($input: DisableUserInput!) {
  enableDriver(input: $input)
}
    `;

export const useEnableDriverMutation = <
      TError = unknown,
      TContext = unknown
    >(options?: UseMutationOptions<operations.EnableDriverMutation, TError, operations.EnableDriverMutationVariables, TContext>) => {
    
    return useMutation<operations.EnableDriverMutation, TError, operations.EnableDriverMutationVariables, TContext>(
      {
    mutationKey: ['EnableDriver'],
    mutationFn: (variables?: operations.EnableDriverMutationVariables) => graphqlFetcher<operations.EnableDriverMutation, operations.EnableDriverMutationVariables>(EnableDriverDocument, variables)(),
    ...options
  }
    )};

export const FindAllDriversDocument = `
    query FindAllDrivers($input: GetAllInput!) {
  findAllDrivers(GetAllInput: $input) {
    total
    records {
      id
      publicId
      email
      phoneNumber
      firstName
      lastName
      avatar
      confirmedByAlb
      disabled
      freelancer
      company
      siret
      birthDate
      drivingLicenseDate
      drivingPermitNumber
      mcqScore
      iban
      fullName
      business_status
      driverType
      nbDeals
      docs
      createdAt
    }
  }
}
    `;

export const useFindAllDriversQuery = <
      TData = operations.FindAllDriversQuery,
      TError = unknown
    >(
      variables: operations.FindAllDriversQueryVariables,
      options?: Omit<UseQueryOptions<operations.FindAllDriversQuery, TError, TData>, 'queryKey'> & { queryKey?: UseQueryOptions<operations.FindAllDriversQuery, TError, TData>['queryKey'] }
    ) => {
    
    return useQuery<operations.FindAllDriversQuery, TError, TData>(
      {
    queryKey: ['FindAllDrivers', variables],
    queryFn: graphqlFetcher<operations.FindAllDriversQuery, operations.FindAllDriversQueryVariables>(FindAllDriversDocument, variables),
    ...options
  }
    )};

useFindAllDriversQuery.document = FindAllDriversDocument;

useFindAllDriversQuery.getKey = (variables: operations.FindAllDriversQueryVariables) => ['FindAllDrivers', variables];

export const DeleteDriverDocument = `
    mutation deleteDriver($DeleteInput: DeleteInput!) {
  deleteDriver(DeleteInput: $DeleteInput) {
    id
  }
}
    `;

export const useDeleteDriverMutation = <
      TError = unknown,
      TContext = unknown
    >(options?: UseMutationOptions<operations.DeleteDriverMutation, TError, operations.DeleteDriverMutationVariables, TContext>) => {
    
    return useMutation<operations.DeleteDriverMutation, TError, operations.DeleteDriverMutationVariables, TContext>(
      {
    mutationKey: ['deleteDriver'],
    mutationFn: (variables?: operations.DeleteDriverMutationVariables) => graphqlFetcher<operations.DeleteDriverMutation, operations.DeleteDriverMutationVariables>(DeleteDriverDocument, variables)(),
    ...options
  }
    )};

export const GetDriverTermsDataDocument = `
    query GetDriverTermsData($driverId: Int!) {
  getDriverTermsData(driverId: $driverId) {
    lastUpdated
    allAccepted
    sections {
      id
      title
      content
      subsections
      accepted
    }
  }
}
    `;

export const useGetDriverTermsDataQuery = <
      TData = operations.GetDriverTermsDataQuery,
      TError = unknown
    >(
      variables: operations.GetDriverTermsDataQueryVariables,
      options?: Omit<UseQueryOptions<operations.GetDriverTermsDataQuery, TError, TData>, 'queryKey'> & { queryKey?: UseQueryOptions<operations.GetDriverTermsDataQuery, TError, TData>['queryKey'] }
    ) => {
    
    return useQuery<operations.GetDriverTermsDataQuery, TError, TData>(
      {
    queryKey: ['GetDriverTermsData', variables],
    queryFn: graphqlFetcher<operations.GetDriverTermsDataQuery, operations.GetDriverTermsDataQueryVariables>(GetDriverTermsDataDocument, variables),
    ...options
  }
    )};

useGetDriverTermsDataQuery.document = GetDriverTermsDataDocument;

useGetDriverTermsDataQuery.getKey = (variables: operations.GetDriverTermsDataQueryVariables) => ['GetDriverTermsData', variables];

export const FindAllOffersDocument = `
    query findAllOffers($GetAllInput: GetAllInput!) {
  findAllOffers(GetAllInput: $GetAllInput) {
    total
    records {
      id
      deliveryId
      demandedPrice
      proposedByDriver
      proposedByAdminId
      driverId
      status
      accepted
      createdAt
      driver {
        firstName
        lastName
      }
    }
  }
}
    `;

export const useFindAllOffersQuery = <
      TData = operations.FindAllOffersQuery,
      TError = unknown
    >(
      variables: operations.FindAllOffersQueryVariables,
      options?: Omit<UseQueryOptions<operations.FindAllOffersQuery, TError, TData>, 'queryKey'> & { queryKey?: UseQueryOptions<operations.FindAllOffersQuery, TError, TData>['queryKey'] }
    ) => {
    
    return useQuery<operations.FindAllOffersQuery, TError, TData>(
      {
    queryKey: ['findAllOffers', variables],
    queryFn: graphqlFetcher<operations.FindAllOffersQuery, operations.FindAllOffersQueryVariables>(FindAllOffersDocument, variables),
    ...options
  }
    )};

useFindAllOffersQuery.document = FindAllOffersDocument;

useFindAllOffersQuery.getKey = (variables: operations.FindAllOffersQueryVariables) => ['findAllOffers', variables];

export const AcceptOfferDocument = `
    mutation acceptOffer($AcceptOfferInput: AcceptOfferInput!) {
  acceptOffer(AcceptOfferInput: $AcceptOfferInput) {
    id
    demandedPrice
    deliveryId
    driverId
    status
  }
}
    `;

export const useAcceptOfferMutation = <
      TError = unknown,
      TContext = unknown
    >(options?: UseMutationOptions<operations.AcceptOfferMutation, TError, operations.AcceptOfferMutationVariables, TContext>) => {
    
    return useMutation<operations.AcceptOfferMutation, TError, operations.AcceptOfferMutationVariables, TContext>(
      {
    mutationKey: ['acceptOffer'],
    mutationFn: (variables?: operations.AcceptOfferMutationVariables) => graphqlFetcher<operations.AcceptOfferMutation, operations.AcceptOfferMutationVariables>(AcceptOfferDocument, variables)(),
    ...options
  }
    )};

export const DeleteOfferDocument = `
    mutation deleteOffer($DeleteInput: DeleteInput!) {
  deleteOffer(DeleteInput: $DeleteInput) {
    id
    driverId
    deliveryId
    status
  }
}
    `;

export const useDeleteOfferMutation = <
      TError = unknown,
      TContext = unknown
    >(options?: UseMutationOptions<operations.DeleteOfferMutation, TError, operations.DeleteOfferMutationVariables, TContext>) => {
    
    return useMutation<operations.DeleteOfferMutation, TError, operations.DeleteOfferMutationVariables, TContext>(
      {
    mutationKey: ['deleteOffer'],
    mutationFn: (variables?: operations.DeleteOfferMutationVariables) => graphqlFetcher<operations.DeleteOfferMutation, operations.DeleteOfferMutationVariables>(DeleteOfferDocument, variables)(),
    ...options
  }
    )};

export const RejectOfferDocument = `
    mutation rejectOffer($RejectOfferInput: RejectOfferInput!) {
  rejectOffer(RejectOfferInput: $RejectOfferInput) {
    id
    driverId
    deliveryId
    status
  }
}
    `;

export const useRejectOfferMutation = <
      TError = unknown,
      TContext = unknown
    >(options?: UseMutationOptions<operations.RejectOfferMutation, TError, operations.RejectOfferMutationVariables, TContext>) => {
    
    return useMutation<operations.RejectOfferMutation, TError, operations.RejectOfferMutationVariables, TContext>(
      {
    mutationKey: ['rejectOffer'],
    mutationFn: (variables?: operations.RejectOfferMutationVariables) => graphqlFetcher<operations.RejectOfferMutation, operations.RejectOfferMutationVariables>(RejectOfferDocument, variables)(),
    ...options
  }
    )};

export const CreateOfferDocument = `
    mutation createOffer($CreateOfferInput: CreateOfferInput!) {
  createOffer(CreateOfferInput: $CreateOfferInput) {
    id
    demandedPrice
    driverId
    deliveryId
    accepted
    status
  }
}
    `;

export const useCreateOfferMutation = <
      TError = unknown,
      TContext = unknown
    >(options?: UseMutationOptions<operations.CreateOfferMutation, TError, operations.CreateOfferMutationVariables, TContext>) => {
    
    return useMutation<operations.CreateOfferMutation, TError, operations.CreateOfferMutationVariables, TContext>(
      {
    mutationKey: ['createOffer'],
    mutationFn: (variables?: operations.CreateOfferMutationVariables) => graphqlFetcher<operations.CreateOfferMutation, operations.CreateOfferMutationVariables>(CreateOfferDocument, variables)(),
    ...options
  }
    )};

export const FindAllOrganizationsDocument = `
    query findAllOrganizations($GetAllInput: GetAllInput!) {
  findAllOrganizations(GetAllInput: $GetAllInput) {
    total
    records {
      name
      slug
      owner {
        id
        assigneeFirstName
        assigneeLastName
        assigneeEmail
        assigneePhone
        createdAt
        sub
      }
      members_count
      deliveries_count
      createdAt
    }
  }
}
    `;

export const useFindAllOrganizationsQuery = <
      TData = operations.FindAllOrganizationsQuery,
      TError = unknown
    >(
      variables: operations.FindAllOrganizationsQueryVariables,
      options?: Omit<UseQueryOptions<operations.FindAllOrganizationsQuery, TError, TData>, 'queryKey'> & { queryKey?: UseQueryOptions<operations.FindAllOrganizationsQuery, TError, TData>['queryKey'] }
    ) => {
    
    return useQuery<operations.FindAllOrganizationsQuery, TError, TData>(
      {
    queryKey: ['findAllOrganizations', variables],
    queryFn: graphqlFetcher<operations.FindAllOrganizationsQuery, operations.FindAllOrganizationsQueryVariables>(FindAllOrganizationsDocument, variables),
    ...options
  }
    )};

useFindAllOrganizationsQuery.document = FindAllOrganizationsDocument;

useFindAllOrganizationsQuery.getKey = (variables: operations.FindAllOrganizationsQueryVariables) => ['findAllOrganizations', variables];

export const FindOrganizationBySlugDocument = `
    query findOrganizationBySlug($slug: String!) {
  findOrganizationBySlug(slug: $slug) {
    name
    slug
    owner {
      id
      assigneeFirstName
      assigneePhone
      assigneeFirstName
      assigneeLastName
      assigneeFunction
      assigneeService
      assigneeRank
      assigneeEmail
      assigneePhone
      createdAt
      updatedAt
      address {
        id
        postalCode
        city
        address
        latitude
        longitude
        place_id
        country
      }
    }
    settings
  }
}
    `;

export const useFindOrganizationBySlugQuery = <
      TData = operations.FindOrganizationBySlugQuery,
      TError = unknown
    >(
      variables: operations.FindOrganizationBySlugQueryVariables,
      options?: Omit<UseQueryOptions<operations.FindOrganizationBySlugQuery, TError, TData>, 'queryKey'> & { queryKey?: UseQueryOptions<operations.FindOrganizationBySlugQuery, TError, TData>['queryKey'] }
    ) => {
    
    return useQuery<operations.FindOrganizationBySlugQuery, TError, TData>(
      {
    queryKey: ['findOrganizationBySlug', variables],
    queryFn: graphqlFetcher<operations.FindOrganizationBySlugQuery, operations.FindOrganizationBySlugQueryVariables>(FindOrganizationBySlugDocument, variables),
    ...options
  }
    )};

useFindOrganizationBySlugQuery.document = FindOrganizationBySlugDocument;

useFindOrganizationBySlugQuery.getKey = (variables: operations.FindOrganizationBySlugQueryVariables) => ['findOrganizationBySlug', variables];

export const FindMeDocument = `
    query findMe {
  findMe {
    id
    assigneeFirstName
    assigneeLastName
    assigneeFunction
    assigneeService
    assigneeRank
    assigneeEmail
    assigneePhone
    createdAt
    updatedAt
  }
}
    `;

export const useFindMeQuery = <
      TData = operations.FindMeQuery,
      TError = unknown
    >(
      variables?: operations.FindMeQueryVariables,
      options?: Omit<UseQueryOptions<operations.FindMeQuery, TError, TData>, 'queryKey'> & { queryKey?: UseQueryOptions<operations.FindMeQuery, TError, TData>['queryKey'] }
    ) => {
    
    return useQuery<operations.FindMeQuery, TError, TData>(
      {
    queryKey: variables === undefined ? ['findMe'] : ['findMe', variables],
    queryFn: graphqlFetcher<operations.FindMeQuery, operations.FindMeQueryVariables>(FindMeDocument, variables),
    ...options
  }
    )};

useFindMeQuery.document = FindMeDocument;

useFindMeQuery.getKey = (variables?: operations.FindMeQueryVariables) => variables === undefined ? ['findMe'] : ['findMe', variables];

export const MembersDocument = `
    query members($GetMembersByRankInput: GetMembersByRankInput!) {
  members(input: $GetMembersByRankInput) {
    total
    records {
      id
      assigneeFirstName
      assigneeLastName
      assigneeFunction
      assigneeService
      assigneeRank
      assigneeEmail
      assigneePhone
      createdAt
      updatedAt
    }
  }
}
    `;

export const useMembersQuery = <
      TData = operations.MembersQuery,
      TError = unknown
    >(
      variables: operations.MembersQueryVariables,
      options?: Omit<UseQueryOptions<operations.MembersQuery, TError, TData>, 'queryKey'> & { queryKey?: UseQueryOptions<operations.MembersQuery, TError, TData>['queryKey'] }
    ) => {
    
    return useQuery<operations.MembersQuery, TError, TData>(
      {
    queryKey: ['members', variables],
    queryFn: graphqlFetcher<operations.MembersQuery, operations.MembersQueryVariables>(MembersDocument, variables),
    ...options
  }
    )};

useMembersQuery.document = MembersDocument;

useMembersQuery.getKey = (variables: operations.MembersQueryVariables) => ['members', variables];

export const UpdateProfileDocument = `
    mutation updateProfile($UpdateProfileInput: UpdateProfileInput!) {
  updateProfile(UpdateProfileInput: $UpdateProfileInput) {
    id
    assigneeFirstName
    assigneeLastName
    assigneeFunction
    assigneeService
    assigneeRank
    assigneeEmail
    assigneePhone
    createdAt
    updatedAt
  }
}
    `;

export const useUpdateProfileMutation = <
      TError = unknown,
      TContext = unknown
    >(options?: UseMutationOptions<operations.UpdateProfileMutation, TError, operations.UpdateProfileMutationVariables, TContext>) => {
    
    return useMutation<operations.UpdateProfileMutation, TError, operations.UpdateProfileMutationVariables, TContext>(
      {
    mutationKey: ['updateProfile'],
    mutationFn: (variables?: operations.UpdateProfileMutationVariables) => graphqlFetcher<operations.UpdateProfileMutation, operations.UpdateProfileMutationVariables>(UpdateProfileDocument, variables)(),
    ...options
  }
    )};

export const AddMemberToOrganizationDocument = `
    mutation addMemberToOrganization($AddMemberToOrganizationInput: AddMemberToOrganizationInput!) {
  addMemberToOrganization(
    AddMemberToOrganizationInput: $AddMemberToOrganizationInput
  ) {
    id
    assigneeFirstName
    assigneeLastName
    assigneeFunction
    assigneeService
    assigneeRank
    assigneeEmail
    assigneePhone
    createdAt
    updatedAt
  }
}
    `;

export const useAddMemberToOrganizationMutation = <
      TError = unknown,
      TContext = unknown
    >(options?: UseMutationOptions<operations.AddMemberToOrganizationMutation, TError, operations.AddMemberToOrganizationMutationVariables, TContext>) => {
    
    return useMutation<operations.AddMemberToOrganizationMutation, TError, operations.AddMemberToOrganizationMutationVariables, TContext>(
      {
    mutationKey: ['addMemberToOrganization'],
    mutationFn: (variables?: operations.AddMemberToOrganizationMutationVariables) => graphqlFetcher<operations.AddMemberToOrganizationMutation, operations.AddMemberToOrganizationMutationVariables>(AddMemberToOrganizationDocument, variables)(),
    ...options
  }
    )};

export const UpdateMemberDocument = `
    mutation updateMember($UpdateMemberInput: UpdateMemberInput!) {
  updateMember(UpdateMemberInput: $UpdateMemberInput) {
    id
    assigneeFirstName
    assigneeLastName
    assigneeFunction
    assigneeService
    assigneeRank
    assigneeEmail
    assigneePhone
    createdAt
    updatedAt
  }
}
    `;

export const useUpdateMemberMutation = <
      TError = unknown,
      TContext = unknown
    >(options?: UseMutationOptions<operations.UpdateMemberMutation, TError, operations.UpdateMemberMutationVariables, TContext>) => {
    
    return useMutation<operations.UpdateMemberMutation, TError, operations.UpdateMemberMutationVariables, TContext>(
      {
    mutationKey: ['updateMember'],
    mutationFn: (variables?: operations.UpdateMemberMutationVariables) => graphqlFetcher<operations.UpdateMemberMutation, operations.UpdateMemberMutationVariables>(UpdateMemberDocument, variables)(),
    ...options
  }
    )};

export const DeleteMemberDocument = `
    mutation deleteMember($id: Float!) {
  deleteMember(id: $id) {
    success
    message
    deletedId
  }
}
    `;

export const useDeleteMemberMutation = <
      TError = unknown,
      TContext = unknown
    >(options?: UseMutationOptions<operations.DeleteMemberMutation, TError, operations.DeleteMemberMutationVariables, TContext>) => {
    
    return useMutation<operations.DeleteMemberMutation, TError, operations.DeleteMemberMutationVariables, TContext>(
      {
    mutationKey: ['deleteMember'],
    mutationFn: (variables?: operations.DeleteMemberMutationVariables) => graphqlFetcher<operations.DeleteMemberMutation, operations.DeleteMemberMutationVariables>(DeleteMemberDocument, variables)(),
    ...options
  }
    )};

export const CreateOrganizationDocument = `
    mutation createOrganization($CreateOrganizationInput: CreateOrganizationInput!) {
  createOrganization(CreateOrganizationInput: $CreateOrganizationInput) {
    name
    slug
    settings
    owner {
      id
      assigneeFirstName
      assigneeLastName
      assigneeFunction
      assigneeService
      assigneeEmail
      assigneePhone
      address {
        id
        postalCode
        city
        address
        latitude
        longitude
        place_id
      }
    }
  }
}
    `;

export const useCreateOrganizationMutation = <
      TError = unknown,
      TContext = unknown
    >(options?: UseMutationOptions<operations.CreateOrganizationMutation, TError, operations.CreateOrganizationMutationVariables, TContext>) => {
    
    return useMutation<operations.CreateOrganizationMutation, TError, operations.CreateOrganizationMutationVariables, TContext>(
      {
    mutationKey: ['createOrganization'],
    mutationFn: (variables?: operations.CreateOrganizationMutationVariables) => graphqlFetcher<operations.CreateOrganizationMutation, operations.CreateOrganizationMutationVariables>(CreateOrganizationDocument, variables)(),
    ...options
  }
    )};

export const UpdateOrganizationDocument = `
    mutation updateOrganization($UpdateOrganizationInput: UpdateOrganizationInput!) {
  updateOrganization(UpdateOrganizationInput: $UpdateOrganizationInput) {
    name
    slug
    settings
    owner {
      id
      assigneeFirstName
      assigneeLastName
      assigneeFunction
      assigneeService
      assigneeEmail
      assigneePhone
      address {
        id
        postalCode
        city
        address
        latitude
        longitude
        place_id
      }
    }
  }
}
    `;

export const useUpdateOrganizationMutation = <
      TError = unknown,
      TContext = unknown
    >(options?: UseMutationOptions<operations.UpdateOrganizationMutation, TError, operations.UpdateOrganizationMutationVariables, TContext>) => {
    
    return useMutation<operations.UpdateOrganizationMutation, TError, operations.UpdateOrganizationMutationVariables, TContext>(
      {
    mutationKey: ['updateOrganization'],
    mutationFn: (variables?: operations.UpdateOrganizationMutationVariables) => graphqlFetcher<operations.UpdateOrganizationMutation, operations.UpdateOrganizationMutationVariables>(UpdateOrganizationDocument, variables)(),
    ...options
  }
    )};

export const DeleteOrganizationDocument = `
    mutation deleteOrganization($slug: String!) {
  deleteOrganization(slug: $slug) {
    success
    message
    deletedId
  }
}
    `;

export const useDeleteOrganizationMutation = <
      TError = unknown,
      TContext = unknown
    >(options?: UseMutationOptions<operations.DeleteOrganizationMutation, TError, operations.DeleteOrganizationMutationVariables, TContext>) => {
    
    return useMutation<operations.DeleteOrganizationMutation, TError, operations.DeleteOrganizationMutationVariables, TContext>(
      {
    mutationKey: ['deleteOrganization'],
    mutationFn: (variables?: operations.DeleteOrganizationMutationVariables) => graphqlFetcher<operations.DeleteOrganizationMutation, operations.DeleteOrganizationMutationVariables>(DeleteOrganizationDocument, variables)(),
    ...options
  }
    )};

export const FindAllDeliveryPaymentsDocument = `
    query findAllDeliveryPayments($GetAllInput: GetAllInput!) {
  findAllDeliveryPayments(GetAllInput: $GetAllInput) {
    total
    records {
      id
      createdAt
      amount
      status
      deliveryId
      driver {
        fullName
      }
    }
  }
}
    `;

export const useFindAllDeliveryPaymentsQuery = <
      TData = operations.FindAllDeliveryPaymentsQuery,
      TError = unknown
    >(
      variables: operations.FindAllDeliveryPaymentsQueryVariables,
      options?: Omit<UseQueryOptions<operations.FindAllDeliveryPaymentsQuery, TError, TData>, 'queryKey'> & { queryKey?: UseQueryOptions<operations.FindAllDeliveryPaymentsQuery, TError, TData>['queryKey'] }
    ) => {
    
    return useQuery<operations.FindAllDeliveryPaymentsQuery, TError, TData>(
      {
    queryKey: ['findAllDeliveryPayments', variables],
    queryFn: graphqlFetcher<operations.FindAllDeliveryPaymentsQuery, operations.FindAllDeliveryPaymentsQueryVariables>(FindAllDeliveryPaymentsDocument, variables),
    ...options
  }
    )};

useFindAllDeliveryPaymentsQuery.document = FindAllDeliveryPaymentsDocument;

useFindAllDeliveryPaymentsQuery.getKey = (variables: operations.FindAllDeliveryPaymentsQueryVariables) => ['findAllDeliveryPayments', variables];

export const GetPublicTermsDataDocument = `
    query GetPublicTermsData {
  getPublicTermsData {
    lastUpdated
    version
    title
    sections {
      id
      title
      content
      subsections
    }
  }
}
    `;

export const useGetPublicTermsDataQuery = <
      TData = operations.GetPublicTermsDataQuery,
      TError = unknown
    >(
      variables?: operations.GetPublicTermsDataQueryVariables,
      options?: Omit<UseQueryOptions<operations.GetPublicTermsDataQuery, TError, TData>, 'queryKey'> & { queryKey?: UseQueryOptions<operations.GetPublicTermsDataQuery, TError, TData>['queryKey'] }
    ) => {
    
    return useQuery<operations.GetPublicTermsDataQuery, TError, TData>(
      {
    queryKey: variables === undefined ? ['GetPublicTermsData'] : ['GetPublicTermsData', variables],
    queryFn: graphqlFetcher<operations.GetPublicTermsDataQuery, operations.GetPublicTermsDataQueryVariables>(GetPublicTermsDataDocument, variables),
    ...options
  }
    )};

useGetPublicTermsDataQuery.document = GetPublicTermsDataDocument;

useGetPublicTermsDataQuery.getKey = (variables?: operations.GetPublicTermsDataQueryVariables) => variables === undefined ? ['GetPublicTermsData'] : ['GetPublicTermsData', variables];

export const DeleteFileDocument = `
    mutation deleteFile($key: String!) {
  deleteFile(key: $key)
}
    `;

export const useDeleteFileMutation = <
      TError = unknown,
      TContext = unknown
    >(options?: UseMutationOptions<operations.DeleteFileMutation, TError, operations.DeleteFileMutationVariables, TContext>) => {
    
    return useMutation<operations.DeleteFileMutation, TError, operations.DeleteFileMutationVariables, TContext>(
      {
    mutationKey: ['deleteFile'],
    mutationFn: (variables?: operations.DeleteFileMutationVariables) => graphqlFetcher<operations.DeleteFileMutation, operations.DeleteFileMutationVariables>(DeleteFileDocument, variables)(),
    ...options
  }
    )};

export const UpdateVehicleDocument = `
    mutation updateVehicle($updateInput: UpdateVehicleInput!) {
  updateVehicle(UpdateVehicleInput: $updateInput) {
    id
    imagesBefore
    imagesAfter
  }
}
    `;

export const useUpdateVehicleMutation = <
      TError = unknown,
      TContext = unknown
    >(options?: UseMutationOptions<operations.UpdateVehicleMutation, TError, operations.UpdateVehicleMutationVariables, TContext>) => {
    
    return useMutation<operations.UpdateVehicleMutation, TError, operations.UpdateVehicleMutationVariables, TContext>(
      {
    mutationKey: ['updateVehicle'],
    mutationFn: (variables?: operations.UpdateVehicleMutationVariables) => graphqlFetcher<operations.UpdateVehicleMutation, operations.UpdateVehicleMutationVariables>(UpdateVehicleDocument, variables)(),
    ...options
  }
    )};
